import React, { useState, useEffect } from 'react';
import { Card } from "@/components/ui/card";
import { View, ActivityIndicator, Switch, Text, StyleSheet, TouchableOpacity, Pressable } from 'react-native';
import { trpc } from '@/trpc/react';
import Toast from 'react-native-toast-message';
import { useCrew } from '@/context/CrewProvider';
import { Button } from '@/components/ui';
import { Notification } from '../../types/Notification';
import { Ionicons } from '@expo/vector-icons';
import colors from '@/constants/Colors';
import { router } from 'expo-router';
import { JobConfirmationDialog } from '../job-confirmation-dialog';
import { formatLocalTime, toLocalDate, formatLocalDateWithPattern } from 'shared-utils';
import { addHours } from 'date-fns';

interface NewJobProps {
  notification: Notification;
  registerRefetch?: (refetchFn: () => void) => () => void;
}

type JobDate = {
  venue_name: string;
  venue_address_line_1: string;
  venue_address_line_2: string;
  venue_address_line_3: string;
  venue_address_postcode: string;
  venue_what_three_words: string;
  venue_out_of_town_bonus_crew: number;
  date_crew_count: number;
  date_started_at: string;
  date_started_time: number;
  date_hours_needed: number;
  job_date_id: number;
}

type JobData = {
  client_name: string;
  dates: JobDate[]
}

const PENDING_JOB_STATE = 1;
const CHANGES_JOB_STATE = 3;


const mapNotificationToJob = (notification: Notification) => {
  const data = notification.data as JobData;

  const sortableDates = [...data.dates];

  sortableDates.sort((a, b) => {
    const timeA = toLocalDate(a.date_started_at).getTime();
    const timeB = toLocalDate(b.date_started_at).getTime();
    return timeA - timeB;
  });

  return {
    subject: notification.subject,
    data: {
      client_name: data.client_name,
      dates: sortableDates.map((date) => ({
        venue_name: date.venue_name,
        venue_address_line_1: date.venue_address_line_1,
        venue_address_line_2: date.venue_address_line_2,
        venue_address_line_3: date.venue_address_line_3,
        venue_address_postcode: date.venue_address_postcode,
        venue_what_three_words: date.venue_what_three_words,
        venue_out_of_town_bonus_crew: date.venue_out_of_town_bonus_crew,
        date_crew_count: date.date_crew_count,
        date_started_at: date.date_started_at ? formatLocalDateWithPattern(date.date_started_at, 'EEEE dd/MM/yyyy') : null,
        date_started_time: date.date_started_time ? formatLocalTime(new Date(date.date_started_at), "HH:mm") : null,
        date_ended_time: date.date_started_time ? formatLocalTime(addHours(new Date(date.date_started_at), date.date_hours_needed || 0), "HH:mm") : null,
        job_date_id: date.job_date_id
      }))
    },
    subject_type_id: notification.subject_type_id,
  }
}

const NewJob: React.FC<NewJobProps> = ({ notification, registerRefetch }) => {
  const [isPartialAccept, setIsPartialAccept] = useState(false);
  const [acceptedDates, setAcceptedDates] = useState<{ [key: number]: boolean }>({});
  const [expandedCrewDateId, setExpandedCrewDateId] = useState<number | null>(null);
  const [confirmationDialog, setConfirmationDialog] = useState<{
    open: boolean;
    action: 'accept' | 'reject';
    type: 'single' | 'all' | 'partial';
    jobDateCrewId?: number;
  }>({ open: false, action: 'accept', type: 'single' });

  const { currentCrewMember } = useCrew();

  const mappedNotification = mapNotificationToJob(notification);

  const { data: crewList, refetch, isLoading } = trpc.crew.getCrewListForJob.useQuery(mappedNotification.subject_type_id);

  // Pay the crew member can expect for each date. Server side so the rate for their
  // level (as at the shift date) never reaches another crew member's device.
  const { data: payEstimates } = trpc.jobDateListRouter.getPayEstimateForJob.useQuery(mappedNotification.subject_type_id);

  // Register refetch function with parent component
  useEffect(() => {
    if (registerRefetch) {
      return registerRefetch(refetch);
    }
  }, [registerRefetch, refetch]);

  const { mutateAsync: updateStatus, isPending: isUpdatingStatus } = trpc.jobDateListRouter.updateJobCrewDateStatus.useMutation();

  const { mutateAsync: partialAccept, isPending: isPartialAccepting } = trpc.jobDateListRouter.partialAccept.useMutation();

  const isProcessing = isUpdatingStatus || isPartialAccepting;

  const { mutate: markAsRead } = trpc.notification.markAsRead.useMutation();

  const findJobCrewId = (jobDateId: number) =>
    crewList?.find((crew) => crew.crew_id === currentCrewMember?.id && crew.job_date_id === jobDateId)?.id;

  const showError = (text1: string, text2: string) => {
    refetch();
    Toast.show({ type: 'error', text1, text2 });
  }

  // Marking the notification as read is what removes the job request from the inbox, and the
  // inbox is the only place a pending request is ever shown. It must therefore happen only
  // after the response has actually been saved, and only once no date is left pending —
  // otherwise the request vanishes while the office still has the crew member as awaiting reply.
  const markAsReadIfFullyAnswered = (respondedJobCrewIds: number[]) => {
    const stillPending = mappedNotification.data.dates.some(date => {
      const jobCrewId = findJobCrewId(date.job_date_id);
      if (!jobCrewId || respondedJobCrewIds.includes(jobCrewId)) return false;
      return crewList?.find((crew) => crew.id === jobCrewId)?.state === PENDING_JOB_STATE;
    });

    if (!stillPending) {
      markAsRead(notification.id);
    }
  }

  const respondToDates = async (jobCrewIds: number[], status: number, allDatesResolved = true) => {
    try {
      await Promise.all(jobCrewIds.map((params) => updateStatus({ params, status })));
    } catch (error) {
      showError('Something went wrong', error instanceof Error ? error.message : 'Please try again.');
      return;
    }

    // Only clear the request from the inbox when every date it lists was accounted for.
    if (allDatesResolved) {
      markAsReadIfFullyAnswered(jobCrewIds);
    }

    refetch();
    Toast.show({
      type: 'success',
      text1: `Successfully ${status === 2 ? 'accepted' : 'declined'} job.`
    });
  }

  // A date the notification lists but the crew list no longer has means the office changed the
  // booking after the request was sent. Answer the dates that are still valid, but leave the
  // request in the inbox so the rest can be picked up after a refresh.
  const respondToAllDates = (status: number) => {
    const jobCrewIds = mappedNotification.data.dates
      .map((date) => findJobCrewId(date.job_date_id))
      .filter((jobCrewId): jobCrewId is number => !!jobCrewId);

    const allDatesResolved = jobCrewIds.length === mappedNotification.data.dates.length;

    if (jobCrewIds.length === 0) {
      showError('Could not update this job request', 'This job has changed since it was sent. Please refresh and try again.');
      return;
    }

    if (!allDatesResolved) {
      Toast.show({
        type: 'info',
        text1: 'Some dates have changed',
        text2: 'Only the dates still booked to you were updated.'
      });
    }

    respondToDates(jobCrewIds, status, allDatesResolved);
  }

  const onAcceptAll = () => respondToAllDates(2);

  const handleAcceptAll = () => {
    setConfirmationDialog({ open: true, action: 'accept', type: 'all' });
  }

  const onRejectAll = () => respondToAllDates(0);

  const handleRejectAll = () => {
    setConfirmationDialog({ open: true, action: 'reject', type: 'all' });
  }

  const onAccept = (jobDateCrewId: number) => respondToDates([jobDateCrewId], 2);

  const handleAccept = (jobDateCrewId: number) => {
    setConfirmationDialog({ open: true, action: 'accept', type: 'single', jobDateCrewId });
  }

  const onReject = (jobDateCrewId: number) => respondToDates([jobDateCrewId], 0);

  const handleReject = (jobDateCrewId: number) => {
    setConfirmationDialog({ open: true, action: 'reject', type: 'single', jobDateCrewId });
  }

  const onConfirmSelected = async () => {
    const selections = mappedNotification.data.dates.map(date => {
      const jobCrewId = findJobCrewId(date.job_date_id);
      if (!jobCrewId) return null;
      return {
        params: jobCrewId,
        status: acceptedDates[date.job_date_id] ? 2 : 0,
        partialAccept: true
      }
    });

    const resolved = selections.filter((selection) => selection !== null) as { params: number; status: number; partialAccept: boolean }[];

    if (resolved.length === 0) {
      showError('Could not update this job request', 'This job has changed since it was sent. Please refresh and try again.');
      return;
    }

    try {
      await partialAccept(resolved);
    } catch (error) {
      showError('Something went wrong', error instanceof Error ? error.message : 'Please try again.');
      return;
    }

    if (resolved.length === selections.length) {
      markAsReadIfFullyAnswered(resolved.map((selection) => selection.params));
    }

    setIsPartialAccept(false);
    refetch();
    Toast.show({
      type: 'success',
      text1: `Successfully confirmed selected dates.`
    });
  }

  const handleConfirmSelected = () => {
    setConfirmationDialog({ open: true, action: 'accept', type: 'partial' });
  }

  const handleConfirmationAction = () => {
    switch (confirmationDialog.type) {
      case 'single':
        if (confirmationDialog.jobDateCrewId) {
          if (confirmationDialog.action === 'accept') {
            onAccept(confirmationDialog.jobDateCrewId);
          } else {
            onReject(confirmationDialog.jobDateCrewId);
          }
        }
        break;
      case 'all':
        if (confirmationDialog.action === 'accept') {
          onAcceptAll();
        } else {
          onRejectAll();
        }
        break;
      case 'partial':
        onConfirmSelected();
        break;
    }
  }

  const toggleCrewList = (jobDateId: number) => {
    setExpandedCrewDateId(expandedCrewDateId === jobDateId ? null : jobDateId);
  }

  if (isLoading) {
    return (
      <Card style={styles.card}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#888888" />
        </View>
      </Card>
    );
  }

  const isMultipleDates = mappedNotification.data.dates.length > 1;

  const isAllNotPending = mappedNotification.data.dates.every(date => {
    const authedJobCrew = crewList?.find((crew) => crew.crew_id === currentCrewMember?.id && crew.job_date_id === date.job_date_id);
    return authedJobCrew?.state !== PENDING_JOB_STATE;
  });

  const isAnyPending = mappedNotification.data.dates.some(date => {
    const authedJobCrew = crewList?.find((crew) => crew.crew_id === currentCrewMember?.id && crew.job_date_id === date.job_date_id);
    return authedJobCrew?.state === PENDING_JOB_STATE;
  });

  // Until the crew list has loaded every date looks non-pending, which would offer "Mark as read"
  // on a request the crew member has not actually answered yet.
  const canMarkAsRead = !isLoading && !!crewList && !isAnyPending;

  return (
    <>
      <Card style={styles.card}>
        <View style={styles.headerContainer}>
          <View style={styles.iconContainer}>
            <Ionicons name="briefcase" size={24} color="white" />
          </View>
          <Text style={styles.headerTitle}>{mappedNotification.subject}</Text>
        </View>

        {mappedNotification.data.dates.map((date, index) => {
          const authedJobCrew = crewList?.find((crew) => crew.crew_id === currentCrewMember?.id && crew.job_date_id === date.job_date_id);
          const dateCrewList = crewList?.filter(crew => crew.job_date_id === date.job_date_id) || [];
          const pay = payEstimates?.data?.find((estimate) => estimate.job_date_id === date.job_date_id);
          const isCrewListExpanded = expandedCrewDateId === date.job_date_id;

          const isRemovedFromJob = !authedJobCrew;

          return (
            <View key={date.job_date_id}>
              <View style={styles.clientSection}>
                <Text style={styles.clientName}>{mappedNotification.data.client_name}</Text>
              </View>

              <View style={styles.venueSection}>
                <Text style={styles.venueName}>{date.venue_name}</Text>
                <Text style={styles.venueAddress}>
                  {[
                    date.venue_address_line_1,
                    date.venue_address_line_2,
                    date.venue_address_line_3,
                    date.venue_address_postcode
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </Text>
                <Text style={styles.venueAddress}>What3Words: {date.venue_what_three_words}</Text>
              </View>

              {pay && !isRemovedFromJob && (
                <View style={styles.venueDetailsContainer}>
                  {pay.base_pay_unknown ? (
                    <Text style={styles.venueAddress}>
                      We can&apos;t work out the pay for this date. Contact the office for details.
                    </Text>
                  ) : (
                    <View style={styles.payContainer}>
                      <View style={styles.payTotalRow}>
                        <Text style={styles.payTotalLabel}>Pay for this date</Text>
                        <Text style={styles.payTotalValue}>£{pay.total.toFixed(2)}</Text>
                      </View>

                      <View style={styles.payRow}>
                        <Text style={styles.payLabel}>{pay.hours} hours{pay.level_name?.trim() ? ` (${pay.level_name.trim()})` : ''}</Text>
                        <Text style={styles.payValue}>£{pay.base_pay.toFixed(2)}</Text>
                      </View>

                      {pay.experience_bonus > 0 && (
                        <View style={styles.payRow}>
                          <Text style={styles.payLabel}>Experience bonus</Text>
                          <Text style={styles.payValue}>£{pay.experience_bonus.toFixed(2)}</Text>
                        </View>
                      )}

                      {pay.out_of_town_bonus > 0 && (
                        <View style={styles.payRow}>
                          <Text style={styles.payLabel}>OOT bonus</Text>
                          <Text style={styles.payValue}>£{pay.out_of_town_bonus.toFixed(2)}</Text>
                        </View>
                      )}

                      {pay.chief_bonus > 0 && (
                        <View style={styles.payRow}>
                          <Text style={styles.payLabel}>Crew chief bonus</Text>
                          <Text style={styles.payValue}>£{pay.chief_bonus.toFixed(2)}</Text>
                        </View>
                      )}

                      {pay.mileage_fuel > 0 && (
                        <View style={styles.payRow}>
                          <Text style={styles.payLabel}>Mileage fuel</Text>
                          <Text style={styles.payValue}>£{pay.mileage_fuel.toFixed(2)}</Text>
                        </View>
                      )}

                      {pay.driver_bonus > 0 && (
                        <View style={styles.payRow}>
                          <Text style={styles.payLabel}>Driver bonus</Text>
                          <Text style={styles.payValue}>£{pay.driver_bonus.toFixed(2)}</Text>
                        </View>
                      )}

                      {pay.skills_total > 0 && (
                        <View style={styles.payRow}>
                          <Text style={styles.payLabel}>
                            {(authedJobCrew?.job_crew_misc?.length ?? 0) > 0
                              ? `Skills: ${authedJobCrew?.job_crew_misc.map((skill) => skill.name).join(', ')}`
                              : 'Skills'}
                          </Text>
                          <Text style={styles.payValue}>£{pay.skills_total.toFixed(2)}</Text>
                        </View>
                      )}

                      <Text style={styles.payNote}>
                        Based on the hours requested. Extra hours or changes on the day are paid on top.
                      </Text>
                    </View>
                  )}
                </View>
              )}

              <View style={styles.venueDetailsContainer}>
                <TouchableOpacity
                  onPress={() => toggleCrewList(date.job_date_id)}
                  style={styles.crewDropdownButton}
                >
                  <View style={styles.crewButtonTextContainer}>
                    <Text style={[styles.venueAddress, styles.linkText]}>
                      {dateCrewList?.length} crew members requested ({date.date_crew_count} required)
                    </Text>
                  </View>
                  <Ionicons
                    name={isCrewListExpanded ? "chevron-up" : "chevron-down"}
                    size={18}
                    color={colors.primary}
                  />
                </TouchableOpacity>

                {isCrewListExpanded && (
                  <View style={styles.crewListContainer}>
                    {dateCrewList.length === 0 ? (
                      <Text style={styles.crewListEmpty}>No crew members found</Text>
                    ) : (
                      dateCrewList.map((crew) => (
                        <View key={crew.id} style={styles.crewListItem}>
                          <Text numberOfLines={2} style={styles.crewName}>
                            {crew.crew_id === currentCrewMember?.id ?
                              `${crew.crew?.name} (You) ${crew.is_chief ? '(Chief)' : ''}` :
                              `${crew.crew?.name} ${crew.is_chief ? '(Chief)' : ''}`}
                          </Text>
                          <View style={[
                            styles.crewStatusBadge,
                            crew.state === 2 ? styles.acceptedBadge :
                              crew.state === 0 ? styles.declinedBadge :
                                styles.pendingBadge
                          ]}>
                            <Text style={styles.crewStatusText}>
                              {crew.state === 2 ? 'Accepted' :
                                crew.state === 0 ? 'Declined' : 'Pending'}
                            </Text>
                          </View>
                        </View>
                      ))
                    )}
                  </View>
                )}
              </View>

              <View style={styles.contentContainer}>
                <Text style={styles.dateText}>{date.date_started_at}</Text>
                <Text style={styles.timeText}>{date.date_started_time} - {date.date_ended_time}</Text>

                {isRemovedFromJob ? (
                  <Text style={styles.statusText}>You have been removed from this job</Text>
                ) : (
                  <>
                    {isPartialAccept ? (
                      <View style={styles.switchContainer}>
                        <Text style={styles.switchLabel}>Accept this date?</Text>
                        <Switch
                          value={acceptedDates[date.job_date_id] || false}
                          onValueChange={(value) =>
                            setAcceptedDates(prev => ({ ...prev, [date.job_date_id]: value }))
                          }
                          trackColor={{ false: '#e0e0e0', true: '#4CAF50' }}
                          thumbColor="#ffffff"
                        />
                      </View>
                    ) : !isMultipleDates && authedJobCrew?.state === PENDING_JOB_STATE ? (
                      <View style={styles.buttonContainer}>
                        <Button style={styles.acceptButton} variant="success" disabled={isProcessing} onPress={() => handleAccept(authedJobCrew.id)}>
                          <Text style={styles.buttonText}>Accept</Text>
                        </Button>
                        <Button style={styles.declineButton} variant="destructive" disabled={isProcessing} onPress={() => handleReject(authedJobCrew.id)}>
                          <Text style={styles.buttonText}>Decline</Text>
                        </Button>
                      </View>
                    ) : !isPartialAccept && authedJobCrew?.state === CHANGES_JOB_STATE ? (
                      <Text style={[styles.statusText, styles.declinedText]}>
                        This job request has expired. Contact the office to request details.
                      </Text>
                    ) : !isPartialAccept && authedJobCrew?.state !== PENDING_JOB_STATE ? (
                      <Text style={[styles.statusText, authedJobCrew?.state === 2 ? styles.acceptedText : styles.declinedText]}>
                        You have {authedJobCrew?.state === 2 ? "accepted" : "declined"} this job date
                      </Text>
                    ) : null}
                  </>
                )}
              </View>
            </View>
          )
        })}

        {isMultipleDates && !isAllNotPending && !isPartialAccept ? (
          <View style={styles.actionContainer}>
            <Button style={styles.fullWidthButton} variant="success" disabled={isProcessing} onPress={handleAcceptAll}>
              <Text style={styles.buttonText}>Accept All</Text>
            </Button>
            <Button style={styles.fullWidthButton} variant="destructive" disabled={isProcessing} onPress={handleRejectAll}>
              <Text style={styles.buttonText}>Decline All</Text>
            </Button>
            <Button style={styles.fullWidthButton} disabled={isProcessing} onPress={() => setIsPartialAccept(true)}>
              <Text style={styles.buttonText}>Partial Accept</Text>
            </Button>
          </View>
        ) : null}

        {isPartialAccept ? (
          <View style={styles.actionContainer}>
            <Text style={styles.helperText}>Select the dates you would like to accept</Text>
            <Button style={styles.fullWidthButton} variant="success" disabled={isProcessing} onPress={handleConfirmSelected}>
              <Text style={styles.buttonText}>Confirm Selected</Text>
            </Button>
            <Button style={styles.fullWidthButton} variant="destructive" disabled={isProcessing} onPress={() => setIsPartialAccept(false)}>
              <Text style={styles.buttonText}>Cancel</Text>
            </Button>
          </View>
        ) : null}

        <View style={styles.actionContainer}>
          <View style={styles.linkContainer}>
            <Pressable
              className="mt-2 mb-1"
              onPress={() => router.navigate(`/messages`)}
            >
              <Text
                className={`text-sm underline text-blue-600`}
              >
                View messages
              </Text>
            </Pressable>
            {canMarkAsRead && (
              <Pressable
                style={styles.markAsReadButton}
                onPress={() => {
                  markAsRead(notification.id);
                  Toast.show({
                    type: 'success',
                    text1: 'Notification marked as read'
                  });
                }}
              >
                <Text style={styles.markAsReadText}>Mark as read</Text>
              </Pressable>
            )}
          </View>
        </View>
      </Card>

      <JobConfirmationDialog
        open={confirmationDialog.open}
        onOpenChange={(open) => setConfirmationDialog(prev => ({ ...prev, open }))}
        onConfirm={handleConfirmationAction}
        action={confirmationDialog.action}
        jobTitle={mappedNotification.subject}
        clientName={mappedNotification.data.client_name}
        venueName={
          confirmationDialog.type === 'single' && confirmationDialog.jobDateCrewId
            ? (() => {
              const jobCrew = crewList?.find(crew => crew.id === confirmationDialog.jobDateCrewId);
              const date = mappedNotification.data.dates.find(d => d.job_date_id === jobCrew?.job_date_id);
              return date?.venue_name;
            })()
            : mappedNotification.data.dates.length === 1
              ? mappedNotification.data.dates[0]?.venue_name
              : mappedNotification.data.dates[0]?.venue_name // Show first venue for multiple dates
        }
        isMultiple={confirmationDialog.type === 'all' || confirmationDialog.type === 'partial'}
      />
    </>
  );
};

const styles = StyleSheet.create({
  card: {
    borderRadius: 12,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 2,
    borderWidth: 1,
    borderColor: '#eee',
    overflow: 'hidden',
    backgroundColor: '#fff',
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#000000',
  },
  clientSection: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    paddingBottom: 1,
  },
  venueSection: {
    paddingHorizontal: 16,
    paddingBottom: 4,
  },
  contentContainer: {
    padding: 16,
    backgroundColor: '#ffffff',
  },
  actionContainer: {
    padding: 16,
    backgroundColor: '#ffffff',
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  clientName: {
    fontSize: 18,
    fontWeight: '700',
    color: '#000000',
  },
  venueName: {
    fontSize: 18,
    fontWeight: '500',
    color: '#000000',
  },
  venueAddress: {
    fontSize: 16,
    fontWeight: '100',
    color: '#000000',
    marginBottom: 4,
  },
  payContainer: {
    backgroundColor: '#f9f9f9',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#eaeaea',
    padding: 12,
  },
  payTotalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 8,
    marginBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#eaeaea',
  },
  payTotalLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000000',
  },
  payTotalValue: {
    fontSize: 20,
    fontWeight: '700',
    color: '#000000',
  },
  payRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 2,
    gap: 8,
  },
  payLabel: {
    fontSize: 15,
    fontWeight: '300',
    color: '#444444',
    flex: 1,
  },
  payValue: {
    fontSize: 15,
    fontWeight: '400',
    color: '#000000',
  },
  payNote: {
    fontSize: 13,
    color: '#888888',
    marginTop: 8,
  },
  venueDetailsContainer: {
    marginTop: 10,
    paddingHorizontal: 16,
    paddingBottom: 4,
  },
  dateText: {
    fontSize: 16,
    fontWeight: '100',
    color: '#000000',
    marginBottom: 4,
  },
  timeText: {
    fontSize: 18,
    fontWeight: '400',
    color: '#000000',
    marginBottom: 24,
  },
  switchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 16,
  },
  switchLabel: {
    fontSize: 16,
    color: '#444444',
  },
  buttonContainer: {
    flexDirection: 'row',
    marginTop: 8,
  },
  acceptButton: {
    flex: 1,
    marginRight: 16,
    height: 50,
  },
  declineButton: {
    flex: 1,
    backgroundColor: colors.destructive,
    height: 50,
  },
  buttonText: {
    color: '#ffffff',
    fontWeight: '600',
    fontSize: 16,
  },
  statusText: {
    fontSize: 18,
    fontWeight: '500',
    color: '#F44336',
  },
  acceptedText: {
    color: '#4CAF50',
  },
  declinedText: {
    color: '#F44336',
  },
  fullWidthButton: {
    width: '100%',
    marginBottom: 12,
    height: 50,
  },
  helperText: {
    fontSize: 16,
    color: '#444444',
    marginBottom: 16,
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  messageLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
  },
  messageLinkIcon: {
    marginRight: 8,
  },
  messageLinkText: {
    fontSize: 16,
    color: colors.primary,
    fontWeight: '500',
  },
  linkContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  markAsReadButton: {
    padding: 8,
    borderRadius: 4,
    backgroundColor: '#f0f0f0',
  },
  markAsReadText: {
    fontSize: 14,
    color: '#666',
  },
  linkText: {
    color: colors.primary,
    textDecorationLine: 'underline',
  },
  crewDropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingRight: 8,
    borderRadius: 6,
  },
  crewButtonTextContainer: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    flex: 1,
  },
  crewListContainer: {
    marginTop: 8,
    backgroundColor: '#f9f9f9',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#eaeaea',
  },
  crewListItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    flexWrap: 'wrap',
    gap: 8,
  },
  crewListEmpty: {
    textAlign: 'center',
    padding: 12,
    color: '#888',
  },
  crewName: {
    fontSize: 16,
    color: '#333',
    flex: 1,
    marginRight: 8,
    flexWrap: 'wrap',
  },
  crewStatusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  acceptedBadge: {
    backgroundColor: 'rgba(76, 175, 80, 0.2)',
  },
  declinedBadge: {
    backgroundColor: 'rgba(244, 67, 54, 0.2)',
  },
  pendingBadge: {
    backgroundColor: 'rgba(255, 152, 0, 0.2)',
  },
  crewStatusText: {
    fontSize: 14,
    fontWeight: '500',
  },
});

export default NewJob;